/**
 * Find the mate, on the board the famous games are played on.
 *
 * WHAT THIS IS AND WHAT IT IS NOT
 * It is not a puzzle app with a database behind it. A hundred of the games in the
 * library end in mate on the board, so the library is already a hundred
 * positions in which somebody found a mate at the board — and `set.js` walks back
 * from each finish to set the question. The position is real, the answer was
 * found by a person, and the game it came out of is one click away. That is the
 * whole of the idea, and it is why there is no difficulty rating anywhere on this
 * page: the difficulty is whatever Anderssen was up against.
 *
 * IT IS THE SAME BOARD AS THE PLAY PAGES, deliberately and by import. `table.js`
 * is the 3D board, the click-to-move, the promotion chooser, the camera buttons,
 * the tray of taken men and the theme switch, and it already asks its host two
 * questions — may a man be picked up, and here is the move that was chosen. A
 * puzzle is one particular pair of answers to those: yes while you are solving,
 * and "only if it mates" for the move. So this file is an OPPONENT, in the shape
 * `table.js` already expects, that happens to be a referee.
 *
 * WHY A WRONG MOVE IS NOT PLAYED ONTO THE BOARD
 * `table.choose` hands the move over without playing it — the host plays it, by
 * calling `apply`, once it knows it wants to. That is there so the friend page can
 * wait for a relay to echo a move back, and it is exactly what a puzzle needs: a
 * move that is not the mate never happens. The board does not have to be rewound,
 * the score does not have to be edited, and there is no state to be inconsistent
 * for the frame in between.
 */

import { createTable, escapeHtml } from '../play/table.js';
import { uciOf } from '../play/live-game.js';
import { watchUrl } from '../data/games-index.js';
import { playOnUrl } from '../play/from-here.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { buildPuzzles, lengthWord } from './set.js';
import { matingMoves } from './mate.js';
import { celebrate, stopCelebration } from './confetti.js';
import { onMerged, read as readProgress, syncSoon, write as writeProgress } from '../progress.js';

const $ = (id) => document.getElementById(id);

// The nav first, before the wait for three.js: it is the one thing on this page
// that is about the site rather than about the board, and a visitor who arrives
// here and decides they would rather watch a game should not have to wait for a
// WebGL context to be told where the library is.
mountNav($('nav-slot'), { here: 'puzzles', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/**
 * The puzzles, and which of them have been solved.
 *
 * Built before the board rather than after, because it is a third of a second of
 * move generation and the board is several seconds of three.js — so this is free,
 * and the list is on the screen while the stage is still coming up.
 */
const PUZZLES = buildPuzzles();

/**
 * Which ones are done, in `localStorage` under the same `immortal-` prefix as the
 * theme and the page size.
 *
 * IDS AND NOT INDICES. The list is derived from `games.js`, so adding a game
 * renumbers it — and a stored `[0, 4, 9]` would then say somebody had solved
 * three puzzles they have never seen. Ids survive that.
 */
const SOLVED_KEY = 'immortal-solved';

// Kept in this browser, and on the account for whoever has one — see
// `chess-app/progress.js`. Solved puzzles merge by UNION across devices, so
// nothing here can ever unsolve itself.
const solved = new Set(readSolved());

function readSolved() {
  return (readProgress(SOLVED_KEY) || '').split(',').filter(Boolean);
}

function writeSolved() {
  writeProgress(SOLVED_KEY, [...solved].join(','));
}

const table = await createTable();
// The six angles as one button over the board (`../camera-menu.js`).
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

/** solving | won | shown — what the board is doing and what the panel says. */
let phase = 'solving';
/** Which puzzle, as an index into `PUZZLES`. */
let at = 0;
/** How many moves are still to be found. Counts down as a mate in two is played. */
let left = 0;
/** Has anything wrong been tried yet on this one? */
let clean = true;

table.canPick = () => phase === 'solving';
table.onPick = (mv) => { judge(mv); };

// ------------------------------------------------------------------ one puzzle

/** The puzzle on the board right now. */
const now = () => PUZZLES[at];

/**
 * Put a puzzle up.
 *
 * `setScore` rather than a run of `apply` calls: it cuts to the position instead
 * of animating forty moves nobody asked to watch, and it is the one code path the
 * board already uses for "the score is this, catch up".
 */
async function load(index, { scroll = true } = {}) {
  at = ((index % PUZZLES.length) + PUZZLES.length) % PUZZLES.length;
  const puzzle = now();
  stopCelebration();
  phase = 'solving';
  left = puzzle.length;
  clean = true;

  table.you = puzzle.turn;
  // Whose men are on the other side of the board, which on this page is a real
  // person with a name: the material line reads "You have taken … / Kieseritzky
  // has taken …", and being told whose position you have inherited is half of
  // what makes it worth solving.
  table.opponentName = puzzle.turn === 'w' ? puzzle.black : puzzle.white;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore(puzzle.score);
  await table.faceYourSide(true);

  $('verdict').hidden = true;
  $('ask-line').innerHTML = askLine(puzzle);
  $('ask-game').innerHTML = gameLine(puzzle);
  table.setPills([lengthWord(puzzle.length), puzzle.date, puzzle.event]);
  say();
  markList(scroll);
  countUp();
}

/** "White to play, and mate in two." */
function askLine(puzzle) {
  const side = puzzle.turn === 'w' ? 'White' : 'Black';
  return `<b>${side} to play</b>, and ${lengthWord(puzzle.length)}.`;
}

/**
 * Which game this is, said in full.
 *
 * The names are the point. A mate in two out of a puzzle generator is an
 * exercise; the same mate in two labelled "Anderssen — Kieseritzky, London 1851"
 * is a thing that happened, and the difference is most of why this page exists.
 * The link goes to the player at the move BEFORE the puzzle, so following it
 * shows the position being arrived at rather than opening on the answer.
 */
function gameLine(puzzle) {
  const to = watchUrl(puzzle.id, Math.max(1, puzzle.moveNo - 1));
  return `<a href="${escapeHtml(to)}">${escapeHtml(puzzle.title)}</a>`
    + ` &middot; ${escapeHtml(puzzle.white)} &mdash; ${escapeHtml(puzzle.black)}`
    + `, ${escapeHtml(puzzle.event)}, ${escapeHtml(puzzle.date)}`;
}

/** The standing instruction under the board. */
function say() {
  if (phase === 'won') return table.setLead('Mate. Take the next one.');
  if (phase === 'shown') return table.setLead('That was the finish as it was played.');
  table.setLead(left > 1
    ? `Find the move that forces mate in ${left}.`
    : 'One move. Find the mate.');
}

// -------------------------------------------------------------------- judging

/** Is this the same move, whoever generated it? */
const same = (a, b) => a.from === b.from && a.to === b.to && (a.promo || null) === (b.promo || null);

/**
 * A move has been chosen. Is it the mate?
 *
 * `matingMoves` takes no shortcut and is asked of the live position, so ANY move
 * that forces the mate is accepted — not only the one that was played. That
 * costs about twenty milliseconds and buys the thing a puzzle most has to get
 * right: a visitor who finds a second mate is told they found a mate.
 */
async function judge(mv) {
  if (phase !== 'solving') return;
  const pos = live.pos;
  const good = matingMoves(pos, left).some((m) => same(m, mv));
  if (!good) return refuse(pos, mv);

  await table.apply(mv);
  if (live.outcome) return won();

  // A mate in two: the defender has to move, and every move loses. See `defence`.
  await table.apply(defence(mv));
  left -= 1;
  say();
  table.showMoves();
}

/**
 * What the losing side answers with.
 *
 * The game's own defence when the move played was the game's own move, so a
 * visitor who knows the game watches it happen the way it happened. Otherwise the
 * first legal reply — and that choice is cosmetic rather than difficult, because
 * the position was verified as a FORCED mate before it was ever set as a puzzle:
 * every reply loses, so there is no best defence to find.
 */
function defence(mv) {
  const puzzle = now();
  const wanted = uciOf(mv) === puzzle.line[0] ? puzzle.line[1] : null;
  return (wanted && live.fromUci(wanted)) || live.legal()[0];
}

/**
 * Say no, and say why.
 *
 * Naming the move back is the whole of the courtesy here. "Not that one" alone
 * reads as the page not having understood which man was moved, which is the
 * first thing anybody suspects of a 3D board.
 */
function refuse(pos, mv) {
  clean = false;
  const san = pos.san(mv);
  table.say(left > 1
    ? `${san} does not force it. There is a mate in ${left} here.`
    : `${san} is not mate.`);
  table.setTag('not it');
  setTimeout(() => { if (phase === 'solving') table.setTag(''); }, 1400);
}

/** Mate. */
function won() {
  phase = 'won';
  table.state = 'over';
  // The one moment a puzzle is the same thing as the end of one of the videos:
  // there is a mating net on the board, and the closing shot of every published
  // game is a picture of exactly that.
  table.explainMate(true);
  table.setTag('');
  say();
  table.showMoves();
  if (!solved.has(now().id)) { solved.add(now().id); writeSolved(); }
  // Reported every time, not only the first: how OFTEN a mate gets found is the
  // question, and `solved` above is about this browser's own progress.
  count('puzzle-solved', now().id, now().length);
  markList(false);
  countUp();
  verdict(clean ? 'Mate, first time.' : 'Mate.');
  celebrate($('stage-box'), clean ? 'Mate, first time!' : 'Solved!');
}

/** Play the finish rather than find it. Costs the tick. */
async function show() {
  if (phase !== 'solving') return;
  phase = 'shown';
  table.setTag('');
  const puzzle = now();
  // From wherever the board is: a mate in two half-solved has one move left of
  // its line, and `live.ply` against the puzzle's own start says which.
  const played = live.ply - puzzle.score.length;
  for (const uci of puzzle.line.slice(played)) {
    const mv = live.fromUci(uci);
    if (!mv) break;
    await table.apply(mv);
  }
  table.state = 'over';
  table.explainMate(true);
  say();
  table.showMoves();
  markList(false);
  // The pair worth reading together: a puzzle that is shown far more often than it
  // is solved is a puzzle that is too hard, or one whose wording is wrong.
  count('puzzle-shown', now().id, now().length);
  verdict('Shown, not solved.');
}

/**
 * The panel, once it is over.
 *
 * The line in notation, because the answer to "what was it?" is the moves; and a
 * link into the player at the puzzle's own move, because the answer to "how on
 * earth did that position happen?" is the game.
 */
function verdict(headline) {
  const puzzle = now();
  $('verdict-line').textContent = headline;
  $('verdict-why').innerHTML = `${escapeHtml(puzzle.sans.join(' '))}`
    + ` &mdash; ${escapeHtml(puzzle.white)} against ${escapeHtml(puzzle.black)},`
    + ` ${escapeHtml(puzzle.date)}.`;
  // "Play it out" is offered beside the two that were already here, and it is the
  // one for the visitor who was SHOWN the mate rather than the one who found it:
  // being told the answer settles nothing, and the position is right there. It
  // hands the same board to the engine with the same side to move. See
  // `from-here.js`.
  const playOn = playOnUrl({ game: puzzle.id, ply: puzzle.atPly, as: puzzle.turn });
  $('verdict-acts').innerHTML =
    `<a class="ctl" href="${escapeHtml(watchUrl(puzzle.id, puzzle.moveNo))}">Watch the game</a>`
    + `<a class="ctl" href="${escapeHtml(playOn)}" title="Take the position on against the engine">Play it out</a>`
    + '<button class="ctl primary" type="button" data-next="1">Next puzzle</button>';
  $('verdict').hidden = false;
}

// ---------------------------------------------------------------- the list

/**
 * Every puzzle in the library, in the library's order.
 *
 * Buttons rather than links: the board is already up and a puzzle is a change of
 * position on it, not a new document. The one link in this panel is the one that
 * leaves for the player.
 */
function renderList() {
  $('puzzle-list').innerHTML = PUZZLES.map((puzzle, i) => `
    <li>
      <button class="p-row" type="button" data-at="${i}"
        aria-label="${escapeHtml(`${puzzle.title}, ${lengthWord(puzzle.length)}`)}">
        <span class="p-tick" aria-hidden="true"></span>
        <span class="p-swatch" aria-hidden="true"
          ><span style="background:${escapeHtml(puzzle.set?.light || '#eee')}"></span
          ><span style="background:${escapeHtml(puzzle.set?.dark || '#333')}"></span
          ><span style="background:${escapeHtml(puzzle.set?.dark || '#333')}"></span
          ><span style="background:${escapeHtml(puzzle.set?.light || '#eee')}"></span
        ></span>
        <span class="p-name">${escapeHtml(puzzle.title)}</span>
        <span class="p-len" aria-hidden="true">${puzzle.length}</span>
        <span class="p-mate" aria-hidden="true">${escapeHtml(lengthWord(puzzle.length).replace(/^m/, 'M'))}</span>
      </button>
    </li>`).join('');
}

/** Which one is up, and which ones are done. */
function markList(scroll) {
  for (const button of $('puzzle-list').querySelectorAll('.p-row')) {
    const i = Number(button.dataset.at);
    const puzzle = PUZZLES[i];
    button.classList.toggle('on', i === at);
    button.classList.toggle('did', solved.has(puzzle.id));
    if (i === at) button.setAttribute('aria-current', 'true');
    else button.removeAttribute('aria-current');
  }
  if (!scroll) return;
  const on = $('puzzle-list').querySelector('.p-row.on');
  if (!on) return;
  // The list scrolls, and only the list: `scrollIntoView` would scroll every
  // scroller above the row as well, and on a phone that is the page — it opened
  // part way down, with the board's top and the masthead off the screen.
  const list = $('puzzle-list');
  const row = on.getBoundingClientRect();
  const box = list.getBoundingClientRect();
  if (row.top < box.top) list.scrollTop -= box.top - row.top;
  else if (row.bottom > box.bottom) list.scrollTop += row.bottom - box.bottom;
}

function countUp() {
  const n = PUZZLES.filter((p) => solved.has(p.id)).length;
  $('done').textContent = `${n} of ${PUZZLES.length}`;
  // Nothing found yet is not a score worth a badge in the header: it reads as
  // "you have done none of this" before the first puzzle has even been tried.
  $('done').hidden = n === 0;
  $('list-count').textContent = n
    ? `${n} of ${PUZZLES.length} found`
    : `${PUZZLES.length} mates, one per game`;
  $('forget').hidden = n === 0;
}

/**
 * The next one you have not solved, or simply the next one.
 *
 * Wrapping round to look for an unsolved one first is what makes the Next button
 * useful on a second visit: without it, somebody who has done the first twenty
 * presses Next twenty times to reach new ground.
 */
function nextIndex() {
  for (let step = 1; step <= PUZZLES.length; step++) {
    const i = (at + step) % PUZZLES.length;
    if (!solved.has(PUZZLES[i].id)) return i;
  }
  return (at + 1) % PUZZLES.length;
}

// ---------------------------------------------------------------- the wiring

$('next').addEventListener('click', () => load(nextIndex()));
$('show').addEventListener('click', () => show());
$('again').addEventListener('click', () => load(at, { scroll: false }));

$('puzzle-list').addEventListener('click', (event) => {
  const button = event.target.closest('.p-row');
  if (button) load(Number(button.dataset.at), { scroll: false });
});

$('verdict-acts').addEventListener('click', (event) => {
  if (event.target.closest('[data-next]')) load(nextIndex());
});

$('forget').addEventListener('click', () => {
  solved.clear();
  writeSolved();
  markList(false);
  countUp();
});

addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  const key = event.key.toLowerCase();
  if (key === 'n') { event.preventDefault(); load(nextIndex()); }
  if (key === 's') { event.preventDefault(); show(); }
  if (key === 'r') { event.preventDefault(); load(at, { scroll: false }); }
});

// ------------------------------------------------------------------ and go

/**
 * What the account knows, once it has said.
 *
 * The list is drawn from `localStorage` above and then redrawn if a sync brings
 * anything new — a puzzle solved on a phone showing up ticked on a laptop. It is
 * deliberately after the first draw and deliberately not awaited: signed out,
 * offline, or a slow answer all leave the page exactly as it already is.
 */
onMerged(() => {
  const was = solved.size;
  for (const id of readSolved()) solved.add(id);
  if (solved.size !== was) renderList();
});
syncSoon();

renderList();
// The first unsolved one, so coming back lands where you left off rather than
// back at the beginning of a list you have half done. `at` starts at 0, so
// `nextIndex` from there is "the first one that is not done" — unless the first
// one is undone itself, which the check below is for.
await load(solved.has(PUZZLES[0].id) ? nextIndex() : 0);
